import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import type { RiskSignal, SignalSeverity, Vendor } from '@onefootprint/request-types/dashboard';
import { Dialog, Divider, LinkButton, Popover } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import capitalize from 'lodash/capitalize';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import breakAtSeparators from '../../../../utils/break-at-separators';
import { CARD_STATUS_TEXT, type CardStatusTone } from '../../../card-title';
import useRiskSignalSeverityText from '../../hooks/use-risk-signal-severity-text';
import AmlMatches from './components/aml-matches';
import SentilinkContent from './components/sentilink-content';
const SEVERITY_TONE: Record<SignalSeverity, CardStatusTone> = {
  high: 'error',
  medium: 'warning',
  low: 'info',
  info: 'neutral'
};
type RiskSignalItemProps = {
  riskSignal: RiskSignal;
};
const RiskSignalItem = t0 => {
  const $ = _c(77);
  const {
    riskSignal
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings.riskSignals"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const severityT = useRiskSignalSeverityText();
  const titleId = useId();
  const descriptionId = useId();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    id,
    reasonCode,
    severity,
    dataSource,
    description,
    note,
    timestamp
  } = riskSignal;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsPopoverOpen(false);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleEscapeKeyDown = t2;
  let t3;
  if ($[2] !== t) {
    t3 = vendor => {
      const names = {
        idology: () => t(_temp),
        lexis: () => t(_temp2),
        experian: () => t(_temp3),
        twilio: () => t(_temp4),
        middesk: () => t(_temp5),
        incode: () => t(_temp6),
        neuro_id: () => t(_temp7),
        samba_safety: () => t(_temp8),
        sentilink: () => t(_temp9),
        comply_advantage: () => t(_temp10),
        base_layer: () => t(_temp11),
        fingerprint_js: () => t(_temp12),
        taktile: () => t(_temp13),
        resistant: () => t(_temp14)
      };
      return names[vendor]?.() ?? capitalize(vendor.replaceAll("_", " "));
    };
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const getVendorName = t3;
  let t4;
  bb0: {
    if (riskSignal.hasSentilinkDetail) {
      let t5;
      if ($[4] !== t) {
        t5 = t(_temp15);
        $[4] = t;
        $[5] = t5;
      } else {
        t5 = $[5];
      }
      let t6;
      if ($[6] !== t) {
        t6 = t(_temp16);
        $[6] = t;
        $[7] = t6;
      } else {
        t6 = $[7];
      }
      let t7;
      if ($[8] !== id) {
        t7 = <SentilinkContent signalId={id} />;
        $[8] = id;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      let t8;
      if ($[10] !== t5 || $[11] !== t6 || $[12] !== t7) {
        t8 = {
          label: t5,
          title: t6,
          content: t7
        };
        $[10] = t5;
        $[11] = t6;
        $[12] = t7;
        $[13] = t8;
      } else {
        t8 = $[13];
      }
      t4 = t8;
      break bb0;
    }
    if (riskSignal.hasAmlHits) {
      let t5;
      if ($[14] !== t) {
        t5 = t(_temp17);
        $[14] = t;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      let t6;
      if ($[16] !== t) {
        t6 = t(_temp18);
        $[16] = t;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      let t7;
      if ($[18] !== id) {
        t7 = <AmlMatches riskSignalId={id} />;
        $[18] = id;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      let t8;
      if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
        t8 = {
          label: t5,
          title: t6,
          content: t7
        };
        $[20] = t5;
        $[21] = t6;
        $[22] = t7;
        $[23] = t8;
      } else {
        t8 = $[23];
      }
      t4 = t8;
      break bb0;
    }
    t4 = null;
  }
  const dialogConfig = t4;
  let t5;
  if ($[24] !== dataSource || $[25] !== getVendorName) {
    t5 = dataSource === "footprint" ? undefined : getVendorName(dataSource);
    $[24] = dataSource;
    $[25] = getVendorName;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  const source = t5;
  const t6 = note || reasonCode;
  let t7;
  if ($[27] !== t6 || $[28] !== titleId) {
    t7 = <p id={titleId} className="text-label-2 text-primary">{t6}</p>;
    $[27] = t6;
    $[28] = titleId;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== description || $[31] !== descriptionId) {
    t8 = <p id={descriptionId} className="mt-2 text-pretty text-body-3 text-tertiary">{description}</p>;
    $[30] = description;
    $[31] = descriptionId;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const t9 = source && `${source} · `;
  let t10;
  if ($[33] !== formatDateWithTime || $[34] !== timestamp) {
    t10 = formatDateWithTime(new Date(timestamp), {
      month: "short",
      year: "numeric"
    });
    $[33] = formatDateWithTime;
    $[34] = timestamp;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== timestamp) {
    t11 = <time dateTime={timestamp}>{t10}</time>;
    $[36] = t10;
    $[37] = timestamp;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== t11 || $[40] !== t9) {
    t12 = <p className="mt-3 text-caption-4 text-tertiary">{t9}{t11}</p>;
    $[39] = t11;
    $[40] = t9;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== dialogConfig) {
    t13 = dialogConfig && <><Divider className="my-3" /><LinkButton variant="label-3" className="self-start" onClick={() => setIsDialogOpen(true)}>{dialogConfig.label}</LinkButton></>;
    $[42] = dialogConfig;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== descriptionId || $[45] !== t12 || $[46] !== t13 || $[47] !== t7 || $[48] !== t8 || $[49] !== titleId) {
    t14 = <div role="dialog" aria-labelledby={titleId} aria-describedby={descriptionId} className="flex w-88 max-w-[calc(100vw-2rem)] flex-col px-4 py-3">{t7}{t8}{t12}{t13}</div>;
    $[44] = descriptionId;
    $[45] = t12;
    $[46] = t13;
    $[47] = t7;
    $[48] = t8;
    $[49] = titleId;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  const PopoverContent = t14;
  let t15;
  if ($[51] !== reasonCode) {
    t15 = breakAtSeparators(reasonCode);
    $[51] = reasonCode;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t15) {
    t16 = <span className="min-w-0 text-snippet-2 text-secondary wrap-anywhere group-hover:text-primary group-data-[state=open]:text-primary">{t15}</span>;
    $[53] = t15;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span aria-hidden={true} className="mb-1 min-w-4 flex-1 border-b border-dashed border-tertiary" />;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  const t18 = CARD_STATUS_TEXT[SEVERITY_TONE[severity]];
  let t19;
  if ($[56] !== t18) {
    t19 = cx("shrink-0 text-label-3", t18);
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== severity || $[59] !== severityT) {
    t20 = severityT(severity);
    $[58] = severity;
    $[59] = severityT;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== t19 || $[62] !== t20) {
    t21 = <span className={t19}>{t20}</span>;
    $[61] = t19;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t16 || $[65] !== t21) {
    t22 = <button type="button" className="group flex w-full cursor-pointer items-end gap-2 rounded-default px-2 py-1 text-left transition-colors duration-100 hover:bg-hover data-[state=open]:bg-hover">{t16}{" "}{t17}{t21}</button>;
    $[64] = t16;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== PopoverContent || $[68] !== isPopoverOpen || $[69] !== t22) {
    t23 = <Popover content={PopoverContent} className="rounded-default!" onEscapeKeyDown={handleEscapeKeyDown} onOpenAutoFocus={_temp19} controlledOpen={isPopoverOpen} onControlledOpenChange={setIsPopoverOpen}>{t22}</Popover>;
    $[67] = PopoverContent;
    $[68] = isPopoverOpen;
    $[69] = t22;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== dialogConfig || $[72] !== isDialogOpen) {
    t24 = isDialogOpen && dialogConfig && <Dialog title={dialogConfig.title} open={true} onClose={() => setIsDialogOpen(false)}>{dialogConfig.content}</Dialog>;
    $[71] = dialogConfig;
    $[72] = isDialogOpen;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== t23 || $[75] !== t24) {
    t25 = <>{t23}{t24}</>;
    $[74] = t23;
    $[75] = t24;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  return t25;
};
export { SEVERITY_TONE };
export default RiskSignalItem;
function _temp($) {
  return $.vendor.idology;
}
function _temp2($_0) {
  return $_0.vendor.lexis;
}
function _temp3($_1) {
  return $_1.vendor.experian;
}
function _temp4($_2) {
  return $_2.vendor.twilio;
}
function _temp5($_3) {
  return $_3.vendor.middesk;
}
function _temp6($_4) {
  return $_4.vendor.incode;
}
function _temp7($_5) {
  return $_5.vendor.neuro_id;
}
function _temp8($_6) {
  return $_6.vendor.samba_safety;
}
function _temp9($_7) {
  return $_7.vendor.sentilink;
}
function _temp10($_8) {
  return $_8.vendor.comply_advantage;
}
function _temp11($_9) {
  return $_9.vendor.base_layer;
}
function _temp12($_10) {
  return $_10.vendor.fingerprint_js;
}
function _temp13($_11) {
  return $_11.vendor.taktile;
}
function _temp14($_12) {
  return $_12.vendor.resistant;
}
function _temp15($_13) {
  return $_13.details.viewSignalScores;
}
function _temp16($_14) {
  return $_14.dialogs.sentilink;
}
function _temp17($_15) {
  return $_15.details.viewMatches;
}
function _temp18($_16) {
  return $_16.dialogs.amlMedia;
}
function _temp19(event) {
  event.preventDefault();
  (event.currentTarget as HTMLElement | null)?.focus();
}